import { useCreateVetterEntityMutation } from '@codezo/graphql';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogForm,
	DialogHeader,
	DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';

export function InviteVetterModal({
	open,
	onOpenChange,
	onSuccess,
	initialName,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onSuccess?: (vetter: { id: string; name: string }) => void;
	/** Prefills the name, e.g. with what was typed into a selector's search. */
	initialName?: string;
}) {
	const [name, setName] = useState('');
	const [description, setDescription] = useState('');
	const [notes, setNotes] = useState('');
	const [isActive, setIsActive] = useState(true);

	const [createVetterEntity, { loading }] = useCreateVetterEntityMutation();

	useEffect(() => {
		if (open) setName(initialName ?? '');
		// eslint-disable-next-line react-hooks/exhaustive-deps -- seed the name only when the modal opens, not while typing
	}, [open]);

	const resetForm = () => {
		setName('');
		setDescription('');
		setNotes('');
		setIsActive(true);
	};

	const handleSubmit = async () => {
		if (!name.trim()) return;

		try {
			const { data } = await createVetterEntity({
				variables: {
					input: {
						name: name.trim(),
						description: description.trim() || undefined,
						notes: notes.trim() || undefined,
						isActive,
					},
				},
			});

			const res = data?.createVetterEntity;
			if (res?.success) {
				// referenceKey is the new vetter's id.
				const created = { id: res.referenceKey ?? '', name: name.trim() };
				toast.success('Vetter created successfully');
				resetForm();
				onOpenChange(false);
				onSuccess?.(created);
			} else {
				toast.error(res?.message || 'Failed to create vetter');
			}
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to create vetter');
		}
	};

	return (
		<Dialog
			open={open}
			onOpenChange={(next) => {
				if (!next) resetForm();
				onOpenChange(next);
			}}
		>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Add vetter</DialogTitle>
					<DialogDescription>
						Create a new vetter entity. Add team members afterwards, then pick one as
						the primary contact.
					</DialogDescription>
				</DialogHeader>

				<DialogForm onSubmit={handleSubmit}>
					<DialogBody>
						<div className="flex flex-col gap-4">
							<div className="flex flex-col gap-1.5">
								<Label htmlFor="vetter-name">
									Name <span className="text-destructive">*</span>
								</Label>
								<Input
									id="vetter-name"
									value={name}
									onChange={(e) => setName(e.target.value)}
									placeholder="e.g. Quality Inspections Ltd"
								/>
							</div>

							<div className="flex flex-col gap-1.5">
								<Label htmlFor="vetter-description">Description</Label>
								<Textarea
									id="vetter-description"
									value={description}
									onChange={(e) => setDescription(e.target.value)}
									placeholder="Brief description of this vetter entity"
									rows={3}
								/>
							</div>

							<div className="flex flex-col gap-1.5">
								<Label htmlFor="vetter-notes">Notes</Label>
								<Textarea
									id="vetter-notes"
									value={notes}
									onChange={(e) => setNotes(e.target.value)}
									placeholder="Anything worth remembering about this vetter"
									rows={3}
								/>
							</div>

							<div className="flex items-center justify-between rounded-lg border border-line-2 px-3 py-2.5">
								<div>
									<Label htmlFor="vetter-active" className="text-sm">
										Active
									</Label>
									<p className="text-sm text-muted-foreground">
										Set this vetter as active immediately
									</p>
								</div>
								<Switch
									id="vetter-active"
									checked={isActive}
									onCheckedChange={setIsActive}
								/>
							</div>
						</div>
					</DialogBody>

					<DialogFooter>
						<Button
							type="button"
							variant="outline"
							onClick={() => {
								resetForm();
								onOpenChange(false);
							}}
						>
							Cancel
						</Button>
						<Button type="submit" disabled={!name.trim() || loading}>
							{loading ? 'Creating…' : 'Create vetter'}
						</Button>
					</DialogFooter>
				</DialogForm>
			</DialogContent>
		</Dialog>
	);
}
